/**
 * Route guard. Used from `beforeLoad`, so a signed-out visitor never renders a
 * protected screen at all.
 *
 * WHY IT ASKS THE SERVER. It used to decide from VITE_DATA_SOURCE, a setting
 * baked into the browser bundle at build time. That setting was never set on
 * the live deployment, so the live site's browser code believed it was a demo
 * and switched the guard off entirely. Asking the server who you are removes
 * the build-time flag, and with it the whole class of "the bundle and the
 * server disagree" bug:
 *   - in mock mode the server answers with the demo identity, so this passes;
 *   - in db mode it answers 401 when there is no session, so this redirects.
 */
import { ORPCError } from '@orpc/client'
import type { QueryClient } from '@tanstack/react-query'
import { redirect } from '@tanstack/react-router'
import { stopImpersonatingRequest } from '@/features/impersonation/api'
import { meQueryOptions } from './use-me'

export async function requireSession(pathname: string, queryClient: QueryClient): Promise<void> {
  try {
    await queryClient.ensureQueryData(meQueryOptions())
  } catch (error) {
    if (error instanceof ORPCError && error.status === 401) {
      // An impersonation that ran out its hour while the tab was closed leaves
      // no session — but the admin's own is still set aside. Put it back
      // rather than send them to sign in (ADR impersonation). One request, and a no-op
      // for anyone who wasn't impersonating.
      if ((await stopImpersonatingRequest()).ok) {
        queryClient.clear()
        await queryClient.ensureQueryData(meQueryOptions())
        return
      }
      throw redirect({ to: '/sign-in', search: { redirect: pathname } })
    }
    // Anything else — a dropped connection, a server error — is not proof the
    // person is signed out. Let the screen render and show its own error state
    // rather than bouncing a signed-in person to the sign-in page.
  }
}
